import { SortingState } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import {
  fetchSupplierStockOverview,
  exportSupplierStockCSV,
} from "slices/Inventory/Instock/SupplierStock/thunk";
import { clearSupplierStockOverview } from "slices/Inventory/Instock/SupplierStock/reducer";

import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoadingSpinner from "components/LoadingSpinner";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";

type SupplierStockListingProps = {
  warehouseId: any;
};

const SupplierStockListing: React.FC<SupplierStockListingProps> = ({
  warehouseId,
}) => {
  document.title = "Inventory";

  const tableState = usePreservedTableState({
    key: TableKeys.INVENTORY.IN_STOCK.SUPPLIER_STOCK,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [tableInstance, setTableInstance] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const dispatch = useDispatch<any>();

  const selectSupplierStock = createSelector(
    (state: any) => state.inventorySupplierStockOverviewSlice,
    (item) => ({
      list: item?.supplierStockOverviewList || [],
      rowCount: item?.rowCount || 0,
      loading: item?.loading,
      error: item?.error,
    }),
  );

  const { list, rowCount, loading, error } = useSelector(selectSupplierStock);

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.inventory?.app_name,
    app_modules?.inventory?.modules?.in_stock,
  );

  useEffect(() => {
    if (warehouseId) {
      dispatch(
        fetchSupplierStockOverview({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortDirection as "asc" | "desc",
          search,
          filters: filterValue,
          warehouseId: warehouseId,
        }),
      );
    } else {
      dispatch(clearSupplierStockOverview());
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    filterValue,
    warehouseId,
  ]);

  // Debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  const columns = useMemo(() => {
    return [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Supplier Name",
        accessorKey: "supplier_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.SupplierStock?.FILTER_DROPDOWN(
            "supplier_name",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.supplier_name || "-",
      },
      {
        header: "Products",
        accessorKey: "product_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.SupplierStock?.FILTER_DROPDOWN(
            "product_name",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => {
          return row.original.product_name || "-";
        },
      },
      {
        header: "Product SKU",
        accessorKey: "product_sku",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.SupplierStock?.FILTER_DROPDOWN(
            "product_sku",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => {
          return row.original.product_sku || "-";
        },
      },
      {
        header: "Category",
        accessorKey: "category",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.SupplierStock?.FILTER_DROPDOWN(
            "category",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => {
          return row.original.category || "-";
        },
      },
      {
        header: "Sub-Category",
        accessorKey: "sub_category",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.SupplierStock?.FILTER_DROPDOWN(
            "sub_category",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => {
          return row.original.sub_category || "-";
        },
      },
      {
        header: "Make",
        accessorKey: "make",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.SupplierStock?.FILTER_DROPDOWN(
            "make",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => {
          return row.original.make || "-";
        },
      },
      {
        header: "Model",
        accessorKey: "model",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.SupplierStock?.FILTER_DROPDOWN(
            "model",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => {
          return row.original.model || "-";
        },
      },
      {
        header: "Unit",
        accessorKey: "unit",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.INVENTORY?.IN_STOCK?.SupplierStock?.FILTER_DROPDOWN(
            "unit",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.unit || "-",
      },
      {
        header: "Total Received Stock",
        accessorKey: "total_stock",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) =>
          `${row.original.total_stock?.toLocaleString()}`,
      },
      {
        header: "Good Stock",
        accessorKey: "available_qty",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) =>
          `${row.original.available_qty?.toLocaleString()}`,
      },
      {
        header: "Damaged Stock",
        accessorKey: "damaged_qty",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) =>
          `${row.original.damaged_qty?.toLocaleString()}`,
      },
      
    ];
  }, [list, warehouseId]);

  return (
    <div
      className="d-flex flex-column"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
      }}
    >
      <div className="flex-shrink-0">
        <Row className="align-items-center mecord-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search..."
          />

          <Col
            xs={12}
            md={12}
            lg={8}
            xl={7}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
              />
            )}
            {canView && (
              <ExportButton
                onExport={async () => {
                  return await exportSupplierStockCSV({
                    warehouse_id: warehouseId,
                    search,
                    filters: filterValue,
                  });
                }}
              />
            )}
          </Col>
        </Row>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          marginTop: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="text-center py-4 text-danger">
            <h5>Error: {error}</h5>
          </div>
        ) : (
          <MasterTableContainer
            columns={columns}
            data={list || []}
            totalRecords={rowCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
            sorting={sorting}
            onSortingChange={setSorting}
            isBordered={false}
            isPagination={true}
            manualSorting={true}
            onTableReady={setTableInstance}
          />
        )}
      </div>
    </div>
  );
};

export default SupplierStockListing;
